"use client";

import Button, { geistSans } from "@/components/atoms/buttons/Button";
import { toast } from "@/components/atoms/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import { cn } from "@/lib/utils";
import { createAuthSchemas, NewPasswordSchema } from "@/lib/validation/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState, useTransition } from "react";
import { useForm } from "react-hook-form";

type ResetPasswordFormProps = {
  locale: Locales;
};

const ResetPasswordForm = ({ locale }: ResetPasswordFormProps) => {
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [isConfirmPassVisible, setIsConfirmPassVisible] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const { newPasswordSchema } = createAuthSchemas(locale);
  const form = useForm<NewPasswordSchema>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = form;

  const onSubmit = async (data: NewPasswordSchema) => {
    if (!token) {
      toast.error({ title: __("Something went wrong, Please try again later.", locale) });
      return;
    }

    if (!email) {
      toast.error({ title: __("Email is required", locale) });
      return;
    }

    startTransition(async () => {
      try {
        await sdk.auth.updateProvider(
          "customer",
          "emailpass",
          {
            email,
            password: data.password,
          },
          token
        );

        toast.success({ title: __("Password reset successfully!", locale) });
        reset();
        router.push(getPermalink(locale, "login"));
      } catch (error) {
        if (error instanceof SyntaxError && error.message.includes("Unexpected end of JSON input")) {
          toast.success({ title: __("Password reset successfully!", locale) });
          return;
        }

        console.warn(error);
        toast.error({ title: __("Something went wrong, Please try again later.", locale) });
      }
    });
  };

  return (
    <div className="font-cabinet-grotesk text-dark-primary mx-auto mt-10 w-full max-w-[672px] space-y-5.5">
      <h3 className="text-2xl font-bold">{__("New Password", locale)}</h3>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className={cn("space-y-6", geistSans.className)}>
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-4">
            <FormField
              control={control}
              name="password"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.confirmPassword && !errors.password && "mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("New Password", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={isPending}
                          type={isPassVisible ? "text" : "password"}
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                          {...field}
                        />
                        <button
                          onClick={() => setIsPassVisible((state) => !state)}
                          type="button"
                          className="absolute inset-y-0 end-0 cursor-pointer pe-3 pl-3"
                        >
                          {isPassVisible ? (
                            <EyeOff size={20} className="text-dark-primary" />
                          ) : (
                            <Eye size={20} className="text-dark-primary" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className={cn("w-full gap-1", errors.password && !errors.confirmPassword && "mb-6")}>
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Repeat Password", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={isPending}
                          type={isConfirmPassVisible ? "text" : "password"}
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                          {...field}
                        />
                        <button
                          onClick={() => setIsConfirmPassVisible((state) => !state)}
                          type="button"
                          className="absolute inset-y-0 end-0 cursor-pointer pe-3 pl-3"
                        >
                          {isConfirmPassVisible ? (
                            <EyeOff size={20} className="text-dark-primary" />
                          ) : (
                            <Eye size={20} className="text-dark-primary" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button disabled={isPending} className="h-11 w-full text-base font-semibold" type="submit">
            {isPending && <Loader2 className="mr-2 animate-spin" />} {__("Submit", locale)}
          </Button>
        </form>
      </Form>
      <p>
        {__("Already registered ?", locale)}{" "}
        <Link
          href={getPermalink(locale, "login")}
          className="text-pink-primary focus-visible:ring-pink-primary/50 text-start underline outline-none focus-visible:ring-[2px] focus-visible:ring-offset-2"
        >
          {__("Log in", locale)}
        </Link>
      </p>
    </div>
  );
};

export default ResetPasswordForm;
